import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTonWallet, useTonConnectUI } from '@tonconnect/ui-react'
import type { Model } from '../lib/api'
import { BottomSheet, ProviderBadge, CocoonBadge } from './ui'
import CreateKeySheet from './CreateKeySheet'
import { chatStorage } from '../lib/chat-storage'
import { haptics } from '../lib/haptics'
import ModelOnboardingStories from './ModelOnboardingStories'

interface Props {
  model: Model | null
  open: boolean
  onClose: () => void
  apiKey?: string | null
  activeKeyName?: string
}

function formatCtx(ctx: number): string {
  if (ctx >= 1_000_000) return `${(ctx / 1_000_000).toFixed(0)}M`
  if (ctx >= 1_000) return `${(ctx / 1_000).toFixed(0)}K`
  return `${ctx}`
}

function formatPrice(usdPerToken: number): string {
  const perM = usdPerToken * 1_000_000
  if (perM === 0) return '$0/M'
  if (perM < 1) return `$${perM.toFixed(3)}/M`
  return `$${perM.toFixed(2)}/M`
}

function formatCredits(per1k: number): string {
  if (per1k >= 1000) return `${(per1k / 1000).toFixed(1)}K`
  return `${per1k}`
}

type SnippetLang = 'curl' | 'python' | 'js'

const BASE_URL = 'https://imago.market/v1'

/** Returns display-safe key (truncated) or placeholder */
function displayKey(apiKey?: string | null): string {
  if (!apiKey) return 'YOUR_KEY'
  return apiKey.slice(0, 14) + '...'
}

function getSnippet(modelId: string, lang: SnippetLang, apiKey?: string | null): string {
  const key = displayKey(apiKey)
  if (lang === 'curl') {
    return `curl ${BASE_URL}/chat/completions \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${modelId}",
    "messages": [{"role":"user","content":"Hello"}]
  }'`
  }
  if (lang === 'python') {
    return `from openai import OpenAI

client = OpenAI(
    base_url="${BASE_URL}",
    api_key="${key}",
)

resp = client.chat.completions.create(
    model="${modelId}",
    messages=[{"role": "user", "content": "Hello"}],
)
print(resp.choices[0].message.content)`
  }
  return `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "${BASE_URL}",
  apiKey: "${key}",
});

const resp = await client.chat.completions.create({
  model: "${modelId}",
  messages: [{ role: "user", content: "Hello" }],
});
console.log(resp.choices[0].message.content);`
}

/** One-liner for clipboard — uses full real key */
function getSnippetOneliner(modelId: string, lang: SnippetLang, apiKey?: string | null): string {
  const key = apiKey || 'YOUR_KEY'
  if (lang === 'curl') {
    return `curl ${BASE_URL}/chat/completions -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" -d '{"model":"${modelId}","messages":[{"role":"user","content":"Hello"}]}'`
  }
  if (lang === 'python') {
    return `from openai import OpenAI\nclient = OpenAI(base_url="${BASE_URL}", api_key="${key}")\nresp = client.chat.completions.create(model="${modelId}", messages=[{"role": "user", "content": "Hello"}])\nprint(resp.choices[0].message.content)`
  }
  return `import OpenAI from "openai";\nconst client = new OpenAI({ baseURL: "${BASE_URL}", apiKey: "${key}" });\nconst resp = await client.chat.completions.create({ model: "${modelId}", messages: [{ role: "user", content: "Hello" }] });\nconsole.log(resp.choices[0].message.content);`
}

/** Single copy row used in Set up panel */
function CopyRow({
  label,
  value,
  subLabel,
  copyValue,
  copiedLabel,
  onCopy,
  isLast = false,
}: {
  label: string
  value: string
  subLabel?: string
  copyValue: string
  copiedLabel: string | null
  onCopy: (val: string, label: string) => void
  isLast?: boolean
}) {
  return (
    <div
      className="flex items-center justify-between py-sp-3"
      style={!isLast ? { borderBottom: '1px solid var(--tg-theme-secondary-bg-color)' } : undefined}
    >
      <div className="min-w-0 flex-1">
        <p
          className="text-[10px] font-semibold uppercase tracking-wider mb-0.5"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        >
          {label}
        </p>
        <p
          className="text-[12px] font-mono truncate"
          style={{ color: 'var(--tg-theme-text-color)' }}
        >
          {value}
        </p>
        {subLabel && (
          <p className="text-[11px] mt-0.5 truncate" style={{ color: 'var(--tg-theme-hint-color)' }}>
            {subLabel}
          </p>
        )}
      </div>
      <button
        onClick={() => { haptics.notification('success'); onCopy(copyValue, label) }}
        className="shrink-0 ml-sp-3 text-[12px] font-semibold active:opacity-70 transition-opacity"
        style={{
          color: copiedLabel === label
            ? 'var(--tg-theme-hint-color)'
            : 'var(--tg-theme-accent-text-color)',
        }}
      >
        {copiedLabel === label ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  )
}

export default function ModelDetailSheet({ model, open, onClose, apiKey, activeKeyName }: Props) {
  const navigate = useNavigate()
  const wallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()
  const [copiedId, setCopiedId] = useState(false)
  const [createKeyOpen, setCreateKeyOpen] = useState(false)

  const handleCreateKey = () => {
    haptics.impact('light')
    if (!wallet) {
      tonConnectUI.openModal()
      return
    }
    setCreateKeyOpen(true)
  }
  const [showGuide, setShowGuide] = useState(false)
  const [showSetup, setShowSetup] = useState(false)
  const [snippetLang, setSnippetLang] = useState<SnippetLang>('curl')
  const [copiedSetupLabel, setCopiedSetupLabel] = useState<string | null>(null)
  const [copiedSnippet, setCopiedSnippet] = useState(false)

  // Reset all sub-views and copy state when sheet closes
  useEffect(() => {
    if (!open) {
      setShowGuide(false)
      setShowSetup(false)
      setCopiedId(false)
      setCopiedSetupLabel(null)
      setCopiedSnippet(false)
    }
  }, [open])

  if (!model) return null

  const handleChat = () => {
    haptics.impact('medium')
    const session = chatStorage.create(model.id, model.name)
    onClose()
    navigate(`/chat/${session.id}`)
  }

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(model.id)
      haptics.notification('success')
      setCopiedId(true)
      setTimeout(() => setCopiedId(false), 2000)
    } catch {
      haptics.notification('error')
    }
  }

  const handleSetupCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedSetupLabel(label)
      setTimeout(() => setCopiedSetupLabel(null), 2000)
    } catch {
      haptics.notification('error')
    }
  }

  const handleCopySnippet = async () => {
    try {
      await navigator.clipboard.writeText(getSnippetOneliner(model.id, snippetLang, apiKey))
      haptics.notification('success')
      setCopiedSnippet(true)
      setTimeout(() => setCopiedSnippet(false), 2000)
    } catch {
      haptics.notification('error')
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} maxHeight="90vh">
      {/* Step-by-step connection guide — opened via "?" icon */}
      {showGuide && (
        <div onTouchMove={(e) => e.stopPropagation()}>
          <ModelOnboardingStories model={model} onDone={() => setShowGuide(false)} />
        </div>
      )}

      {/* Set up panel — Base URL / API Key / Model ID + snippet */}
      {!showGuide && showSetup && (
        <div className="px-sp-4 pt-sp-3 pb-sp-6">
          {/* Header — 3 columns: Back | centered title | ? */}
          <div className="flex items-center mb-sp-4">
            <div style={{ flex: '1 1 0' }}>
              <button
                onClick={() => { haptics.impact('light'); setShowSetup(false) }}
                className="text-[15px] font-medium active:opacity-70 transition-opacity"
                style={{ color: 'var(--tg-theme-accent-text-color)' }}
              >
                ← Back
              </button>
            </div>
            <span
              className="text-[17px] font-semibold"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              Set up
            </span>
            <div className="flex justify-end" style={{ flex: '1 1 0' }}>
              <button
                onClick={() => { haptics.impact('light'); setShowGuide(true) }}
                className="w-7 h-7 rounded-full flex items-center justify-center active:opacity-60 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
              >
                <span className="text-[13px]" style={{ color: 'var(--tg-theme-hint-color)' }}>?</span>
              </button>
            </div>
          </div>

          {/* Three copy rows */}
          <div
            className="rounded-[14px] px-sp-3 mb-sp-2"
            style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
          >
            <CopyRow
              label="Base URL"
              value={BASE_URL}
              copyValue={BASE_URL}
              copiedLabel={copiedSetupLabel}
              onCopy={handleSetupCopy}
            />
            {apiKey ? (
              <CopyRow
                label="API Key"
                value={displayKey(apiKey)}
                subLabel={activeKeyName}
                copyValue={apiKey}
                copiedLabel={copiedSetupLabel}
                onCopy={handleSetupCopy}
              />
            ) : (
              <div
                className="flex items-center justify-between py-sp-3"
                style={{ borderBottom: '1px solid var(--tg-theme-bg-color)' }}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: 'var(--tg-theme-hint-color)' }}>
                    API Key
                  </p>
                  <p className="text-[12px]" style={{ color: 'var(--tg-theme-hint-color)' }}>
                    Not set yet
                  </p>
                </div>
                <button
                  onClick={handleCreateKey}
                  className="shrink-0 ml-sp-3 text-[12px] font-semibold active:opacity-70 transition-opacity"
                  style={{ color: 'var(--tg-theme-accent-text-color)' }}
                >
                  Create key →
                </button>
              </div>
            )}
            <CopyRow
              label="Model ID"
              value={model.id}
              copyValue={model.id}
              copiedLabel={copiedSetupLabel}
              onCopy={handleSetupCopy}
              isLast
            />
          </div>

          {/* + Create new key — tighter padding so it doesn't float */}
          <button
            onClick={handleCreateKey}
            className="w-full flex items-center justify-center gap-sp-1 pt-sp-2 pb-sp-4 active:opacity-70 transition-opacity"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            <span className="text-[14px] font-medium">+ Create new key</span>
          </button>

          {/* Snippet section */}
          <p
            className="text-[11px] font-semibold uppercase tracking-wider mb-sp-2"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            Quick start
          </p>

          {/* Lang tabs */}
          <div className="flex gap-sp-1 mb-sp-2">
            {(['curl', 'python', 'js'] as SnippetLang[]).map((lang) => (
              <button
                key={lang}
                onClick={() => { haptics.selection(); setSnippetLang(lang) }}
                className="px-sp-2 py-1 rounded-md text-[11px] font-medium active:opacity-70 transition-all"
                style={
                  snippetLang === lang
                    ? { backgroundColor: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }
                    : { backgroundColor: 'var(--tg-theme-secondary-bg-color)', color: 'var(--tg-theme-hint-color)' }
                }
              >
                {lang === 'curl' ? 'cURL' : lang === 'js' ? 'JS/TS' : 'Python'}
              </button>
            ))}
          </div>

          {/* Snippet block */}
          <div
            className="relative rounded-[10px] p-sp-3 overflow-hidden"
            style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
          >
            <pre
              className="text-[11px] font-mono leading-relaxed overflow-x-auto"
              style={{ color: 'var(--tg-theme-text-color)', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {getSnippet(model.id, snippetLang, apiKey)}
            </pre>
            <button
              onClick={handleCopySnippet}
              className="absolute top-sp-2 right-sp-2 text-[10px] font-medium px-sp-2 py-0.5 rounded active:opacity-70 transition-opacity"
              style={{
                backgroundColor: 'var(--tg-theme-bg-color)',
                color: copiedSnippet ? 'var(--tg-theme-hint-color)' : 'var(--tg-theme-accent-text-color)',
              }}
            >
              {copiedSnippet ? '✓' : 'Copy'}
            </button>
          </div>

        </div>
      )}

      {/* Main model detail view */}
      {!showGuide && !showSetup && (
        <div className="px-sp-4 pt-sp-4 pb-sp-6">
          {/* Centered name + provider badge, ? on right */}
          <div className="flex items-center mb-sp-4">
            <div style={{ flex: '1 1 0' }} />
            <div className="flex items-center gap-sp-2">
              <h2
                className="text-[20px] font-semibold"
                style={{ color: 'var(--tg-theme-text-color)' }}
              >
                {model.name}
              </h2>
              {model.id.startsWith('cocoon/')
                ? <CocoonBadge />
                : <ProviderBadge modelId={model.id} size="md" />
              }
            </div>
            <div className="flex justify-end" style={{ flex: '1 1 0' }}>
              <button
                onClick={() => { haptics.impact('light'); setShowGuide(true) }}
                className="w-7 h-7 rounded-full flex items-center justify-center active:opacity-60 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
              >
                <span className="text-[13px]" style={{ color: 'var(--tg-theme-hint-color)' }}>?</span>
              </button>
            </div>
          </div>

          {/* Model ID row */}
          <div
            className="flex items-center justify-between mb-sp-3 px-sp-3 py-sp-2 rounded-[10px]"
            style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
          >
            <code
              className="text-[12px] font-mono flex-1 min-w-0 truncate"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              {model.id}
            </code>
            <button
              onClick={handleCopyId}
              className="shrink-0 ml-sp-3 text-[12px] font-semibold active:opacity-70 transition-opacity"
              style={{ color: copiedId ? 'var(--tg-theme-hint-color)' : 'var(--tg-theme-accent-text-color)' }}
            >
              {copiedId ? '✓ Copied' : 'Copy ID'}
            </button>
          </div>

          {/* Description */}
          {model.description && (
            <p
              className="text-[13px] mb-sp-4"
              style={{ color: 'var(--tg-theme-hint-color)' }}
            >
              {model.description}
            </p>
          )}

          {/* Divider */}
          <div
            className="mb-sp-4"
            style={{ height: '1px', backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
          />

          {/* Stat grid */}
          <div className="grid grid-cols-3 mb-sp-4">
            <div className="flex flex-col pr-sp-3" style={{ borderRight: '1px solid var(--tg-theme-secondary-bg-color)' }}>
              <span className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--tg-theme-hint-color)' }}>
                Context
              </span>
              <span className="text-[17px] font-medium tabular-nums" style={{ color: 'var(--tg-theme-text-color)' }}>
                {formatCtx(model.context_length)}
              </span>
              <span className="text-[11px] tabular-nums mt-0.5" style={{ color: 'var(--tg-theme-hint-color)' }}>
                tokens
              </span>
            </div>

            <div className="flex flex-col px-sp-3" style={{ borderRight: '1px solid var(--tg-theme-secondary-bg-color)' }}>
              <span className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--tg-theme-hint-color)' }}>
                Input
              </span>
              <span className="text-[17px] font-medium tabular-nums" style={{ color: 'var(--tg-theme-text-color)' }}>
                {formatPrice(model.pricing.prompt_usd_per_token)}
              </span>
              <span className="text-[11px] tabular-nums mt-0.5" style={{ color: 'var(--tg-theme-hint-color)' }}>
                {formatCredits(model.pricing.prompt_credits_per_1k)} cr/1K
              </span>
            </div>

            <div className="flex flex-col pl-sp-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--tg-theme-hint-color)' }}>
                Output
              </span>
              <span className="text-[17px] font-medium tabular-nums" style={{ color: 'var(--tg-theme-text-color)' }}>
                {formatPrice(model.pricing.completion_usd_per_token)}
              </span>
              <span className="text-[11px] tabular-nums mt-0.5" style={{ color: 'var(--tg-theme-hint-color)' }}>
                {formatCredits(model.pricing.completion_credits_per_1k)} cr/1K
              </span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-sp-2">
            <button
              onClick={() => { haptics.impact('light'); setShowSetup(true) }}
              className="w-full h-14 rounded-[14px] text-[17px] font-semibold flex items-center justify-center active:opacity-70 transition-opacity"
              style={{
                backgroundColor: 'var(--tg-theme-button-color)',
                color: 'var(--tg-theme-button-text-color)',
              }}
            >
              Set up
            </button>
            <button
              onClick={handleChat}
              className="w-full h-12 rounded-[14px] text-[15px] font-medium flex items-center justify-center active:opacity-70 transition-opacity"
              style={{
                backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                color: 'var(--tg-theme-text-color)',
              }}
            >
              Try in chat →
            </button>
          </div>
        </div>
      )}
      <CreateKeySheet
        open={createKeyOpen}
        onClose={() => setCreateKeyOpen(false)}
      />
    </BottomSheet>
  )
}
